import type { AnyRouter } from '@tanstack/react-router';

/** How often an open tab asks the server for a new version of the app. */
const CHECK_INTERVAL_MS = 15 * 60_000;

/** How long a refresh waits for a new version to install and take over the tab. */
const TAKEOVER_TIMEOUT_MS = 10_000;

/** A new version controls the tab while it still runs the scripts of the old one. */
let updated = false;
const listeners = new Set<() => void>();

function setUpdated(value: boolean): void {
  if (updated === value) return;
  updated = value;
  listeners.forEach((listener) => listener());
}

/** True once a new version controls the tab and the page should load again. */
export function isAppUpdated(): boolean {
  return updated;
}

/** Calls `listener` when a new version arrives (or the watch ends); returns the unsubscribe. */
export function onAppUpdate(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Asks the server for a new service worker, i.e. a new build. Without a worker it does nothing. */
export function checkForAppUpdate(): void {
  if (!('serviceWorker' in navigator)) return;
  void navigator.serviceWorker
    .getRegistration()
    .then((registration) => registration?.update())
    // Without network the check fails; the next one tries again.
    .catch(() => undefined);
}

/**
 * Brings a new build into an open tab (docs/ui/design-system.md, Caching). The service worker
 * installs a new version in the background and takes over at once, but the tab keeps running the
 * old scripts: pages of newly built modules are missing, and lazy parts of the old build are no
 * longer cached. Once a new worker controls the tab, the shell shows a notice with a button to load
 * the page again (`AppUpdateNotice`), and the next page change loads the whole page anyway. The tab
 * never reloads on its own once the person has typed or clicked, so nothing typed gets lost. Before
 * that it reloads at once: right after a deploy the first start still runs the old build from the
 * precache, and a link to a page only the new build knows would end on "Seite nicht gefunden".
 */
export function watchAppUpdates(router: AnyRouter): () => void {
  if (!('serviceWorker' in navigator)) return () => undefined;
  const workers = navigator.serviceWorker;
  // The first worker after installing the app also takes control; that is no new version.
  let controlled = workers.controller !== null;
  let touched = false;
  setUpdated(false);

  const onInput = () => {
    touched = true;
  };
  const onControllerChange = () => {
    if (controlled && !touched) {
      window.location.reload();
      return;
    }
    setUpdated(controlled);
    controlled = true;
  };
  const inputs = ['keydown', 'pointerdown'] as const;
  inputs.forEach((type) => document.addEventListener(type, onInput, { capture: true, once: true }));
  const onVisible = () => {
    if (document.visibilityState === 'visible') checkForAppUpdate();
  };
  workers.addEventListener('controllerchange', onControllerChange);
  document.addEventListener('visibilitychange', onVisible);
  const timer = setInterval(checkForAppUpdate, CHECK_INTERVAL_MS);
  const unsubscribe = router.subscribe('onBeforeNavigate', ({ toLocation, pathChanged }) => {
    if (updated && pathChanged) window.location.replace(toLocation.href);
  });

  return () => {
    workers.removeEventListener('controllerchange', onControllerChange);
    document.removeEventListener('visibilitychange', onVisible);
    inputs.forEach((type) => document.removeEventListener(type, onInput, { capture: true }));
    clearInterval(timer);
    unsubscribe();
    setUpdated(false);
  };
}

/**
 * Asks for a new version now and waits until it controls the tab, for pull to refresh. True when
 * the tab runs an old build and has to load the page again. Without network or service worker
 * false; the regular checks try again.
 */
export async function findAppUpdate(): Promise<boolean> {
  if (updated) return true;
  if (!('serviceWorker' in navigator)) return false;
  const workers = navigator.serviceWorker;
  if (workers.controller === null) return false;
  try {
    const registration = await workers.getRegistration();
    await registration?.update();
    if (!registration?.installing && !registration?.waiting) return updated;
  } catch {
    return false;
  }
  return new Promise((resolve) => {
    const done = (result: boolean) => {
      clearTimeout(timer);
      workers.removeEventListener('controllerchange', onChange);
      resolve(result);
    };
    const onChange = () => done(true);
    const timer = setTimeout(() => done(updated), TAKEOVER_TIMEOUT_MS);
    workers.addEventListener('controllerchange', onChange);
    if (updated) done(true);
  });
}
